import { useDispatchEmail } from "@/hooks/useDispatchEmail";
import { state } from "@/state";
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Stack,
  Text,
  Textarea,
  useToast,
} from "@chakra-ui/react";
import { EmailEvents } from "@root/types/email";
import { useTranslations } from "next-intl";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useSnapshot } from "valtio";

import { deepBlueSky } from "@/styles/theme";

export const FreeDiscoveryCallForm = ({ coachee }: { coachee?: boolean }) => {
  const t = useTranslations();
  const [sending, setSending] = useState(false);
  const toast = useToast();
  const dispatchEmail = useDispatchEmail();
  const { role } = useSnapshot(state);
  const { register, handleSubmit, reset } = useForm();

  const onSubmit = async (data: any) => {
    try {
      setSending(true);
      await dispatchEmail({
        event: EmailEvents.FreeDiscoveryCall,
        ...data,
      });
      toast({
        title: "Success",
        description: "Your message has been sent!",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
      reset({
        fullName: "",
        email: "",
        message: "",
      });
    } catch (e: any) {
      console.error(e);
      toast({
        title: "Error",
        description: "Something went wrong, please try again later.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <Box
      fontFamily={"gotham"}
      bgColor={coachee ? deepBlueSky : "brand.100"}
      w="100%"
    >
      <Box
        py={{ base: 8, md: 16 }}
        px={{ base: 4, md: 16 }}
        pb={{
          base: 8,
          md: 24,
        }}
      >
        <Text
          fontWeight={700}
          fontSize="3xl"
          color={coachee ? "white" : "neutral.900"}
          textAlign={{
            base: "center",
            md: "left",
          }}
          mb={4}
        >
          {t(coachee ? "CoacheePage.freeDiscoveryCall.title" : "tasteDemo")}
        </Text>
        <Text
          fontWeight={300}
          fontSize="3xl"
          color={coachee ? "white" : "neutral.900"}
          textAlign={{
            base: "center",
            md: "left",
          }}
        >
          {t(coachee ? "CoacheePage.freeDiscoveryCall.subtitle" : "orderDemo")}
        </Text>
        {!coachee && (
          <Text
            fontWeight={300}
            fontSize="3xl"
            color={coachee ? "white" : "neutral.900"}
            textAlign={{
              base: "center",
              md: "left",
            }}
          >
            {t(
              coachee ? "CoacheePage.freeDiscoveryCall.subtitle" : "versatility"
            )}
          </Text>
        )}

        <Flex
          mt={{
            base: 8,
            md: 16,
          }}
          w="full"
          direction={["column", "row"]}
          align="flex-start"
          gap={{
            base: 8,
            md: 16,
          }}
        >
          <Stack spacing={5} flex={1} w="full">
            <FormControl>
              <FormLabel
                stroke="0"
                fontSize="sm"
                textTransform={"uppercase"}
                color="brand.100"
              >
                {t("name")}
              </FormLabel>

              <Input
                bgColor="brand.100"
                borderColor="brand.100"
                borderRadius="0"
                width="full"
                px={coachee ? 2 : 0}
                borderBottomColor="neutral.300"
                type="text"
                placeholder={t("name")}
                {...register("fullName", { required: true })}
                _focus={{
                  boxShadow: "none",
                  borderColor: "neutral.300",
                  outline: "none",
                }}
              />
            </FormControl>

            <FormControl>
              <FormLabel
                fontSize="sm"
                textTransform={"uppercase"}
                color="brand.100"
              >
                {t("email")}
              </FormLabel>

              <Input
                width="full"
                bgColor="brand.100"
                borderColor="brand.100"
                borderRadius="0"
                borderBottomColor="neutral.300"
                px={coachee ? 2 : 0}
                type="email"
                placeholder={t("email")}
                {...register("email", { required: true })}
                _focus={{
                  boxShadow: "none",
                  borderColor: "neutral.300",
                  outline: "none",
                }}
              />
            </FormControl>

            {/* <Text fontWeight={700} mt={10} fontSize="3xl" color="neutral.900">
              Ou Ligue-nos para o 96 123 45 67
            </Text> */}
          </Stack>

          <Stack spacing={5} flex={1} w="full">
            <FormControl mb={4}>
              <FormLabel
                fontSize="sm"
                textTransform={"uppercase"}
                color="brand.100"
              >
                {t("message")}
              </FormLabel>

              <Textarea
                bgColor="brand.100"
                borderColor="neutral.300"
                borderRadius="10"
                width="full"
                rows={10}
                placeholder={t("message")}
                borderWidth="1px"
                outline="none"
                _focus={{
                  boxShadow: "none",
                  outline: "none",
                  borderColor: "neutral.300",
                }}
                {...register("message", { required: true })}
              />
            </FormControl>

            <Button
              variant={"primary"}
              fontFamily={"Marlide"}
              fontSize="2xl"
              p={5}
              w={{
                base: "full",
                md: "300px",
              }}
              onClick={handleSubmit(onSubmit)}
              isDisabled={sending}
              isLoading={sending}
            >
              Let´s Clarifayo
            </Button>
          </Stack>
        </Flex>
      </Box>
    </Box>
  );
};
